import { useMemo, useState } from "react";
import type { TypeSafeTableColumns } from "@dealer-center/core-components";
import {
  type IAuditLogEntryView,
  useAccountFranchiseAuditLog
} from "@dealer-center/ddc-pages-account-franchise-audit-log-fetch";
import { useNavigate, useOutletContext } from "react-router-dom";
import type { AccountEntity } from "@dealer-center/ddc-api-account-interface";
import { formatTimeDetail, getFilteredData } from "./helper";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  ClockIcon,
  SearchInput,
  Table,
  Tooltip,
  Typography
} from "@dealer-center/core-components";

const DEFAULT_PAGE_SIZE = 10;

const columns: TypeSafeTableColumns<IAuditLogEntryView> = [
  {
    dataIndex: "accountId",
    title: "Account",
    columnSortable: true
  },
  {
    dataIndex: "franchiseName",
    title: "Franchise",
    columnSortable: true
  },
  {
    dataIndex: "actionType",
    title: "Action Type",
    columnSortable: true
  },
  {
    dataIndex: "formattedModifiedDate",
    title: "Timestamp",
    columnSortable: true,
    defaultSortOrder: "descend",
    sorter: (recordA: IAuditLogEntryView, recordB: IAuditLogEntryView) =>
      new Date(recordA.modifiedDate).getTime() -
      new Date(recordB.modifiedDate).getTime(),
    render: (_text: string, record: IAuditLogEntryView) => (
      <Tooltip
        position="top"
        size="small"
        sx={{ width: "fit-content", whiteSpace: "nowrap", fontSize: "1rem" }}
        toolTipContent={formatTimeDetail(record.modifiedDate)}
      >
        <Box component="span" sx={{ cursor: "default" }}>
          {record.formattedModifiedDate ?? _text}
        </Box>
      </Tooltip>
    )
  },
  {
    dataIndex: "modifiedBy",
    title: "Actor",
    columnSortable: true
  }
];

export const AccountFranchiseAuditLogDisplay = () => {
  const { accountEntity } = useOutletContext<{
    accountEntity: AccountEntity;
  }>();
  const {
    data = [],
    isLoading,
    error
  } = useAccountFranchiseAuditLog({
    accountId: accountEntity.id
  });
  const [filterValue, setFilterValue] = useState("");
  const navigate = useNavigate();

  const filteredData = useMemo(
    () => getFilteredData(data, filterValue.trim(), columns),
    [data, filterValue]
  );

  const handleBackButtonClick = () => {
    navigate(`/dealer/${accountEntity.id}/dashboard/overview`);
  };

  if (error) {
    return (
      <Alert role="alert" sx={{ marginBottom: "16px" }} type="error">
        {error instanceof Error
          ? error.message
          : "Failed to load audit log data"}
      </Alert>
    );
  }

  return (
    <Card
      content={
        <CardContent>
          <Box sx={{ marginBottom: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 1,
                flexWrap: { xs: "wrap", sm: "nowrap" },
                gap: { xs: 1, sm: 2 }
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                  minWidth: 0
                }}
              >
                <ClockIcon
                  style={{
                    fontSize: "20px",
                    marginRight: "8px"
                  }}
                  title="Audit Log"
                />
                <Typography sx={{ fontWeight: "bold" }} variant="h4">
                  Account Audit History
                </Typography>
              </Box>

              <SearchInput
                inputValue={filterValue}
                placeholder="Search audit entries..."
                sx={{ maxWidth: "220px" }}
                onInputChange={event =>
                  setFilterValue(event.target.value ?? "")
                }
              />
            </Box>
          </Box>

          {filteredData?.length === 0 && !isLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: { xs: "120px", md: "150px" },
                color: "text.secondary",
                textAlign: "center",
                padding: { xs: 2, md: 3 }
              }}
            >
              <Box sx={{ marginBottom: { xs: 1, md: 2 } }}>
                <ClockIcon
                  style={{
                    fontSize: "42px",
                    color: "text.disabled"
                  }}
                  title="No audit entries"
                />
              </Box>
              <Typography
                sx={{
                  marginBottom: 1,
                  fontWeight: "medium",
                  fontSize: { xs: "0.875rem", md: "1rem" }
                }}
                variant="body-sm"
              >
                {data?.length === 0
                  ? "No audit entries yet"
                  : "No matching entries found"}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.875rem" }
                }}
                variant="body-xs"
              >
                {data?.length === 0
                  ? "Changes will appear here when account franchises are created or modified"
                  : "Try adjusting your search terms"}
              </Typography>
            </Box>
          ) : (
            <Table.Table
              sortableColumns
              borderType="contained"
              columns={columns}
              data={filteredData || []}
              defaultPageSize={DEFAULT_PAGE_SIZE}
              isLoading={isLoading}
            />
          )}

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}
          >
            <Button
              analyticsConfiguration={{
                eventName: "Account Audit History - Back to Dashboard"
              }}
              buttonStyle="secondary"
              onClick={handleBackButtonClick}
            >
              Back
            </Button>
          </Box>
        </CardContent>
      }
    />
  );
};
